import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import {
  ACTION_ORIGIN_LABELS,
  ACTION_STATUS_LABELS,
  ACTION_STATUSES,
  formatDate,
  planActionSchema,
  projectActionListSchema,
  type ActionStatusName,
  type ProjectAction,
  type ProjectActionList,
  type ProjectActionsQuery,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, KanbanSquare, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

/**
 * Teclado: com o cartão "pego" (Espaço ou Enter), as setas para os lados levam à coluna vizinha (a largura de
 * uma coluna mais o espaço entre elas); para cima e para baixo, um pouco. Espaço solta; Esc cancela.
 */
const columnStep: KeyboardCoordinateGetter = (event, { currentCoordinates, context }) => {
  const column = context.active ? document.querySelector('[data-board-column]') : null;
  const step = column ? column.getBoundingClientRect().width + 12 : 260;
  switch (event.code) {
    case 'ArrowRight':
      event.preventDefault();
      return { ...currentCoordinates, x: currentCoordinates.x + step };
    case 'ArrowLeft':
      event.preventDefault();
      return { ...currentCoordinates, x: currentCoordinates.x - step };
    case 'ArrowDown':
      event.preventDefault();
      return { ...currentCoordinates, y: currentCoordinates.y + 40 };
    case 'ArrowUp':
      event.preventDefault();
      return { ...currentCoordinates, y: currentCoordinates.y - 40 };
    default:
      return undefined;
  }
};

/**
 * Quadro por status das ações dos planos ativos (T-092b, RF-32; DEC-042): arrastar o cartão para outra coluna
 * muda o status (a mesma mudança auditada da lista). Mouse, toque (segurar um instante) e teclado; o seletor
 * "Mover para" do cartão faz o mesmo sem arrastar.
 */
export function ActionBoard({
  base,
  projectId,
  filters,
  canEdit,
  onChanged,
}: {
  /** /organizations/:org/projects/:project/action-plans */
  base: string;
  projectId: string;
  filters: ProjectActionsQuery;
  canEdit: boolean;
  /** Depois de mover: atualizar as outras visões (lista, iniciativas). */
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const key = ['action-plans', projectId, 'board', filters];
  const board = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`${base}/actions`, {
        query: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        schema: projectActionListSchema,
        signal,
      }),
    // Trocar filtro ou período não pisca o carregando: os dados anteriores ficam até chegarem os novos.
    placeholderData: keepPreviousData,
  });

  const move = useMutation({
    meta: { handlesError: true },
    mutationFn: ({ action, status }: { action: ProjectAction; status: ActionStatusName }) =>
      client.request(`${base}/${action.plan.id}/actions/${action.id}/status`, {
        method: 'POST',
        body: { status },
        schema: planActionSchema,
      }),
    // O cartão muda de coluna na hora; se a API recusar, volta (e o motivo aparece).
    onMutate: async ({ action, status }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<ProjectActionList>(key);
      queryClient.setQueryData<ProjectActionList>(key, (data) =>
        data ? { ...data, items: data.items.map((a) => (a.id === action.id ? { ...a, status } : a)) } : data,
      );
      return { before };
    },
    onError: (err, { action }, context) => {
      if (context?.before) queryClient.setQueryData(key, context.before);
      toast.error(`Não foi possível mover "${action.title}"`, { description: errorMessage(err) });
    },
    onSuccess: (a) =>
      toast.success(a.status === 'DONE' ? 'Ação concluída' : 'Status atualizado', {
        description: `${a.title}: ${ACTION_STATUS_LABELS[a.status]}`,
      }),
    onSettled: () => onChanged(),
  });

  const sensors = useSensors(
    // O mouse começa a arrastar ao mover 6 px; no toque é preciso segurar um instante (180 ms), para a
    // rolagem da página não virar arraste por engano.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: columnStep }),
  );
  const items = board.data?.items ?? [];
  const titleOf = (id: unknown) => items.find((a) => a.id === id)?.title ?? 'a ação';
  const columnOf = (id: unknown) => ACTION_STATUS_LABELS[id as ActionStatusName] ?? 'fora das colunas';
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Movendo ${titleOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} sobre a coluna ${columnOf(over.id)}.`
        : `${titleOf(active.id)} fora das colunas.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} solta na coluna ${columnOf(over.id)}.`
        : `${titleOf(active.id)} voltou ao lugar.`,
    onDragCancel: ({ active }) => `Movimento de ${titleOf(active.id)} cancelado.`,
  };

  function onDragEnd({ active, over }: DragEndEvent) {
    const action = items.find((a) => a.id === active.id);
    const status = over?.id as ActionStatusName | undefined;
    if (!action || !status || status === action.status) return;
    move.mutate({ action, status });
  }

  if (board.isPending) return <LoadingState rows={5} label="Carregando o quadro de ações" />;
  if (board.isError) {
    return (
      <ErrorState error={board.error} onRetry={() => void board.refetch()} retrying={board.isFetching} />
    );
  }
  if (board.data.total === 0) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={KanbanSquare}
        title="Nenhuma ação no quadro"
        description="As ações dos planos ativos que passam nos filtros aparecem aqui, uma coluna por status."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {board.data.total > items.length ? (
        <p className="rounded-md bg-muted px-3 py-2 text-sm" role="status">
          Mostrando {items.length} de {board.data.total} ações: use os filtros para ver as demais.
        </p>
      ) : null}
      <DndContext
        sensors={sensors}
        onDragEnd={onDragEnd}
        accessibility={{ announcements }}
        // No celular as colunas rolam de lado: rolar só bem perto da borda (10%), para soltar na coluna que
        // aparece pela metade sem que o quadro ande antes.
        autoScroll={{ threshold: { x: 0.1, y: 0.1 } }}
      >
        <div
          role="region"
          aria-label="Quadro de ações por status"
          className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-5 lg:overflow-visible"
        >
          {ACTION_STATUSES.map((status) => (
            <Column
              key={status}
              status={status}
              actions={items.filter((a) => a.status === status)}
              canEdit={canEdit}
              busy={move.isPending}
              onMove={(action, next) => move.mutate({ action, status: next })}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function Column({
  status,
  actions,
  canEdit,
  busy,
  onMove,
}: {
  status: ActionStatusName;
  actions: ProjectAction[];
  canEdit: boolean;
  busy: boolean;
  onMove: (action: ProjectAction, status: ActionStatusName) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <section
      ref={setNodeRef}
      data-board-column
      aria-label={`${ACTION_STATUS_LABELS[status]} (${actions.length})`}
      className={cn(
        'flex w-72 shrink-0 snap-start flex-col rounded-lg border border-border bg-muted/40 p-2 lg:w-auto',
        isOver && 'border-brand bg-brand/5',
      )}
    >
      <h3 className="flex items-center justify-between px-1 pb-2 text-sm font-semibold">
        {ACTION_STATUS_LABELS[status]}
        <span className="text-xs font-normal text-muted-foreground">{actions.length}</span>
      </h3>
      <ul className="flex min-h-24 flex-col gap-2" aria-label={`Ações em ${ACTION_STATUS_LABELS[status]}`}>
        {actions.map((a) => (
          <Card key={a.id} action={a} canEdit={canEdit} busy={busy} onMove={onMove} />
        ))}
      </ul>
    </section>
  );
}

function Card({
  action: a,
  canEdit,
  busy,
  onMove,
}: {
  action: ProjectAction;
  canEdit: boolean;
  busy: boolean;
  onMove: (action: ProjectAction, status: ActionStatusName) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: a.id,
    // Durante um envio, nada de novo arraste (o estado otimista de um não se mistura com o do outro).
    disabled: !canEdit || busy,
  });
  return (
    <li
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(
        'rounded-md border border-border bg-surface p-2.5 text-sm shadow-sm',
        isDragging && 'relative z-(--z-index-dropdown) opacity-90 shadow-md',
      )}
    >
      <div className="flex items-start gap-1.5">
        {canEdit ? (
          <button
            type="button"
            className="-ml-1 mt-0.5 cursor-grab touch-none rounded p-0.5 text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={`Arrastar ${a.title}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" aria-hidden />
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="font-medium break-words">{a.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {a.plan.name}
            {a.plan.origin.initiative
              ? ` · ${a.plan.origin.initiative.name}`
              : ` · ${ACTION_ORIGIN_LABELS[a.plan.origin.type]}`}
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>{a.responsible?.name ?? 'Sem responsável'}</span>
        <span aria-hidden>·</span>
        <span>{a.dueDate ? `Prazo ${formatDate(a.dueDate)}` : 'Sem prazo'}</span>
        {a.late ? <StatusBadge status="red" label="Atrasada" /> : null}
        {a.requiresEvidence ? (
          <span className="inline-flex items-center gap-0.5" title="Exige evidência para concluir">
            <Paperclip className="size-3.5" aria-hidden />
            {a.evidence ? 'Evidência anexada' : 'Exige evidência'}
          </span>
        ) : null}
      </div>
      {canEdit ? (
        <NativeSelect
          aria-label={`Mover ${a.title} para`}
          value={a.status}
          disabled={busy}
          onChange={(e) => onMove(a, e.target.value as ActionStatusName)}
          className="mt-2 h-8 text-xs"
        >
          {ACTION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {ACTION_STATUS_LABELS[s]}
            </option>
          ))}
        </NativeSelect>
      ) : null}
    </li>
  );
}
